<script setup>
import { computed, onMounted, ref } from 'vue';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  ACELERES_CONNECTION_TYPES,
  SHARED_CONNECTION_TYPES,
  normalizeAceleresConnectionType,
} from 'dashboard/constants/aceleresConnectionTypes';

const store = useStore();
const { accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const isSyncing = ref(false);
const bridgeStatus = ref('idle');
const bridgeMessage = ref('');
const mirroredChannels = ref([]);
const showDisconnectPopup = ref(false);
const selectedInbox = ref({});

const socialCatalog = [
  {
    key: 'instagram',
    label: 'Instagram',
    channelType: INBOX_TYPES.INSTAGRAM,
    route: 'instagram',
    icon: 'i-ri-instagram-line',
    gradient: 'from-[#f58529] via-[#dd2a7b] to-[#8134af]',
    description:
      'Use a conexão OAuth nativa para uma conta Instagram Business ou Creator vinculada a uma Página do Facebook.',
    requirement: 'Conta Business/Creator vinculada a uma Página do Facebook.',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    channelType: INBOX_TYPES.FB,
    route: 'facebook',
    icon: 'i-ri-messenger-line',
    gradient: 'from-[#00c6ff] via-[#1877f2] to-[#243bff]',
    description:
      'Conecte uma Página do Facebook com autorização oficial e seleção da página dentro do próprio Zoraxy.',
    requirement: 'Usuário administrador da Página que será conectada.',
  },
  /*
  {
    key: 'tiktok',
    label: 'TikTok',
    channelType: INBOX_TYPES.TIKTOK,
    route: 'tiktok',
    icon: 'i-ri-tiktok-line',
    gradient: 'from-[#00f2ea] via-[#111827] to-[#ff0050]',
    description:
      'Use o fluxo OAuth nativo do TikTok quando o app TikTok estiver configurado neste ambiente.',
    requirement:
      'App TikTok configurado e conta com permissão para autorizar mensagens.',
  },
  */
  {
    key: 'telegram',
    label: 'Telegram',
    channelType: INBOX_TYPES.TELEGRAM,
    route: 'telegram',
    icon: 'i-ri-telegram-line',
    gradient: 'from-[#38bdf8] via-[#2aabee] to-[#0f4c81]',
    description:
      'Cadastre o token do bot gerado no BotFather. Este é o campo oficial exigido pelo Telegram.',
    requirement: 'Token de bot criado no @BotFather.',
  },
  /*
  {
    key: 'twitter',
    label: 'X (Twitter)',
    channelType: INBOX_TYPES.TWITTER,
    route: 'twitter',
    icon: 'i-ri-twitter-x-line',
    gradient: 'from-[#020617] via-[#334155] to-[#94a3b8]',
    description:
      'Conecte uma conta X Developer App pelo fluxo nativo existente no Zoraxy.',
    requirement: 'Conta e app de desenvolvedor do X configurados.',
  },
  */
];

const CONNECTION_TYPE_RULES = [
  {
    key: ACELERES_CONNECTION_TYPES.INDIVIDUAL_OWN,
    label: ACELERES_CONNECTION_TYPES.INDIVIDUAL_OWN,
    type: 'Individual Próprio',
    description: 'Padrão para número pessoal do vendedor.',
    visibility:
      'Prioridade máxima de privacidade: quem vê é o dono da instância, mais assignee/supervisor que estiver no atendimento no momento.',
    appearsIn:
      'Aparece no painel do dono. Pode aparecer também no painel de quem está atendendo a conversa.',
    icon: 'i-ri-user-shield-line',
  },
  {
    key: ACELERES_CONNECTION_TYPES.INDIVIDUAL_COMPANY,
    label: ACELERES_CONNECTION_TYPES.INDIVIDUAL_COMPANY,
    type: 'Individual da Empresa',
    description:
      'Número corporativo vinculado a um dono, mas com operação de equipe.',
    visibility:
      'Dono e equipe do dono da instância veem a caixa. Gestores fora do time do dono não entram no fluxo por padrão.',
    appearsIn:
      'Aparece no fluxo da equipe do dono da instância; segue regra de atribuição e equipe do Chatwoot.',
    icon: 'i-ri-team-line',
  },
  {
    key: ACELERES_CONNECTION_TYPES.SHARED,
    label: ACELERES_CONNECTION_TYPES.SHARED,
    type: 'Compartilhado',
    description:
      'Número sem vínculo individual próprio ou de empresa. Normalmente usa o fluxo comum de equipe/rodízio.',
    visibility:
      'Segue o fluxo padrão da conta/equipe: atribuição, regras de atendimento e times.',
    appearsIn:
      'Aparece para equipes e perfis autorizados no fluxo comum de conversa.',
    icon: 'i-ri-share-circle-line',
  },
];

const NO_CONNECTION_TYPE = 'connection_type__missing';
const OTHER_CONNECTION_TYPE = 'connection_type__other';
const NO_CONNECTION_TYPE_LABEL = 'Sem connection_type';

const socialInboxes = computed(() => {
  return socialCatalog.map(channel => {
    const records = (inboxes.value || []).filter(
      inbox => inbox.channel_type === channel.channelType
    );
    return {
      ...channel,
      records,
      connected: records.length > 0,
    };
  });
});

const connectedCount = computed(
  () => socialInboxes.value.filter(channel => channel.connected).length
);

const connectionTypeRuleStats = computed(() => {
  const apiInboxes = (inboxes.value || []).filter(
    inbox => inbox.channel_type === INBOX_TYPES.API
  );

  const known = {
    [ACELERES_CONNECTION_TYPES.INDIVIDUAL_OWN]: 0,
    [ACELERES_CONNECTION_TYPES.INDIVIDUAL_COMPANY]: 0,
    [ACELERES_CONNECTION_TYPES.SHARED]: 0,
  };
  const unknown = {};
  let missing = 0;

  apiInboxes.forEach(inbox => {
    const attrs = inbox.additional_attributes || {};
    const ct = normalizeAceleresConnectionType(attrs.connection_type);
    if (!ct) {
      missing += 1;
      return;
    }
    if (Object.prototype.hasOwnProperty.call(known, ct)) {
      known[ct] += 1;
      return;
    }
    if (SHARED_CONNECTION_TYPES.has(ct)) {
      known.shared += 1;
      return;
    }
    unknown[ct] = (unknown[ct] || 0) + 1;
  });

  const unknownRows = Object.entries(unknown).map(([key, count]) => {
    return {
      key: OTHER_CONNECTION_TYPE,
      rawKey: key,
      count,
      label: `${NO_CONNECTION_TYPE_LABEL} (${key})`,
      type: `Tipo extra detectado`,
      description:
        'Existe valor fora do esperado; precisa de regra dedicada para evitar ambiguidade.',
      visibility:
        'Hoje: tratada como fluxo compartilhado para não quebrar operação.',
      appearsIn:
        'Revisar cadastro da conexão para padronizar com uma das opções aceitas.',
      icon: 'i-ri-question-line',
    };
  });

  const sharedCount = known.shared || 0;

  return [
    ...CONNECTION_TYPE_RULES.map(rule => {
      return {
        ...rule,
        count:
          rule.key === ACELERES_CONNECTION_TYPES.SHARED
            ? sharedCount
            : known[rule.key] || 0,
      };
    }),
    {
      key: NO_CONNECTION_TYPE,
      label: NO_CONNECTION_TYPE_LABEL,
      type: 'Canal legado / inválido',
      description:
        'Conexão sem metadado (legacy ou integração parcial). É tratada pelo fluxo padrão de equipe até padronização.',
      visibility: 'Regras padrão da política de conversas e inboxes.',
      appearsIn: 'Visível conforme regra geral da plataforma.',
      icon: 'i-ri-error-warning-line',
      count: missing,
    },
    ...unknownRows,
  ];
});

const resolveAceleresBase = () => resolveAceleresApiBase();

const loadAceleresMirror = async () => {
  try {
    const response = await fetch(
      `${resolveAceleresBase()}/api/integracoes/social-channels`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }
    );
    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      mirroredChannels.value = data.channels || [];
      if (!bridgeMessage.value) {
        bridgeStatus.value = 'success';
        bridgeMessage.value = 'Espelho da Acelerity carregado.';
      }
    }
  } catch {
    bridgeStatus.value = 'idle';
  }
};

const syncWithAceleres = async () => {
  if (isSyncing.value) return;
  isSyncing.value = true;
  bridgeStatus.value = 'syncing';
  bridgeMessage.value = 'Sincronizando espelho na Acelerity...';

  try {
    const response = await fetch(
      `${resolveAceleresBase()}/api/integracoes/social-channels/sync`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || 'Falha ao sincronizar com a Acelerity.');
    }

    bridgeStatus.value = 'success';
    bridgeMessage.value =
      data.message || 'Canais sincronizados com a Acelerity.';
    await loadAceleresMirror();
  } catch (error) {
    bridgeStatus.value = 'error';
    bridgeMessage.value =
      error.message ||
      'Não foi possível sincronizar. Verifique se sua sessão da Acelerity está ativa.';
  } finally {
    isSyncing.value = false;
  }
};

const deleteInbox = async inbox => {
  try {
    await store.dispatch('inboxes/delete', inbox.id);
    useAlert('Canal desconectado.');
    await syncWithAceleres();
  } catch {
    useAlert('Não foi possível desconectar o canal.');
  }
};

const openDisconnect = inbox => {
  selectedInbox.value = inbox;
  showDisconnectPopup.value = true;
};

const closeDisconnect = () => {
  showDisconnectPopup.value = false;
  selectedInbox.value = {};
};

const confirmDisconnect = () => {
  deleteInbox(selectedInbox.value);
  closeDisconnect();
};

const disconnectTitle = computed(() => `Desconectar ${selectedInbox.value.name || 'canal'}?`);
const disconnectMessage = computed(
  () =>
    `A inbox “${selectedInbox.value.name || 'selecionada'}” será removida do Zoraxy. As conversas já existentes permanecem preservadas no histórico.`
);

const mirrorFor = key => {
  return mirroredChannels.value.find(channel => channel.type === key);
};

onMounted(() => {
  store.dispatch('inboxes/get');
  loadAceleresMirror();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="mx-auto flex w-full max-w-7xl flex-col gap-5 px-6 pb-10">
    <section
      class="relative overflow-hidden rounded-3xl border border-n-weak bg-gradient-to-br from-n-slate-12 via-n-slate-11 to-n-blue-11 p-6 text-white shadow-lg"
    >
      <div
        class="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-white/10 blur-2xl"
      />
      <div class="relative grid gap-5 lg:grid-cols-[1.35fr_0.65fr]">
        <div>
          <div
            class="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-white/65"
          >
            Hub de canais
          </div>
          <h2
            class="mb-3 max-w-3xl text-3xl font-semibold tracking-tight text-white"
          >
            Conecte canais sociais usando os fluxos oficiais do Zoraxy.
          </h2>
          <p class="max-w-3xl text-sm leading-6 text-white/90">
            Esta tela concentra Instagram, Facebook e Telegram com os mesmos
            campos suportados por cada plataforma: OAuth quando o canal exige
            autorização e token quando o Telegram exige bot.
          </p>
        </div>
        <div
          class="grid gap-3 rounded-2xl border border-white/15 bg-white/10 p-4"
        >
          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-2xl bg-white/10 p-4">
              <strong class="block text-2xl">{{ connectedCount }}</strong>
              <span class="text-xs text-white/70">canais ativos</span>
            </div>
            <div class="rounded-2xl bg-white/10 p-4">
              <strong class="block text-2xl">{{ socialCatalog.length }}</strong>
              <span class="text-xs text-white/70">canais disponíveis</span>
            </div>
          </div>
          <div class="acl-mobile-account-menu-host flex items-center gap-2">
            <Button
              :is-loading="isSyncing"
              :disabled="isSyncing"
              label="Sincronizar com Acelerity"
              icon="i-lucide-refresh-cw"
              class="min-w-0 flex-1 !rounded-full !bg-white !text-n-slate-12 hover:!bg-white/90"
              @click="syncWithAceleres"
            />
          </div>
        </div>
      </div>
    </section>

    <section
      class="rounded-3xl border border-n-weak bg-n-alpha-2 p-6 shadow-sm"
    >
      <h2 class="mb-4 text-xl font-semibold text-n-slate-12">
        Mapa de visibilidade por tipo de conexão
      </h2>
      <p class="mb-4 text-sm text-n-slate-10">
        Use como referência ao conectar instâncias na Acelerity: os tipos abaixo
        determinam quem pode ver as conversas. Há 3 tipos de regra (mais 1 caso
        legado: conexão sem connection_type/valor inválido).
      </p>
      <div class="grid gap-3 md:grid-cols-2">
        <article
          v-for="rule in connectionTypeRuleStats"
          :key="rule.rawKey || rule.key"
          class="rounded-2xl border border-n-weak bg-white p-4 text-sm"
        >
          <div class="flex items-start gap-3">
            <i :class="rule.icon" class="mt-0.5 text-xl text-n-slate-11" />
            <div class="flex-1">
              <p
                class="mb-1 text-xs uppercase tracking-[0.2em] text-n-slate-10"
              >
                {{ rule.label }}
              </p>
              <h3 class="mb-1 font-semibold text-n-slate-12">
                {{ rule.type }}
              </h3>
              <p class="mb-2 text-n-slate-11">{{ rule.description }}</p>
              <div class="grid gap-1 text-xs text-n-slate-10">
                <p><strong>Visibilidade:</strong> {{ rule.visibility }}</p>
                <p><strong>Como aparece:</strong> {{ rule.appearsIn }}</p>
              </div>
            </div>
            <span
              class="shrink-0 rounded-full bg-n-surface px-2 py-1 text-xs font-semibold text-n-slate-12"
            >
              {{ rule.count }}
            </span>
          </div>
        </article>
      </div>
    </section>

    <div
      v-if="bridgeMessage"
      class="rounded-2xl border px-4 py-3 text-sm"
      :class="{
        'border-n-green-5 bg-n-green-2 text-n-green-11':
          bridgeStatus === 'success',
        'border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11': bridgeStatus === 'error',
        'border-n-amber-5 bg-n-amber-2 text-n-amber-11':
          bridgeStatus === 'syncing',
      }"
    >
      {{ bridgeMessage }}
    </div>

    <section class="grid auto-rows-fr gap-4 lg:grid-cols-2">
      <article
        v-for="channel in socialInboxes"
        :key="channel.key"
        class="flex min-h-[360px] flex-col overflow-hidden rounded-3xl border border-n-weak bg-n-solid-1 shadow-sm"
      >
        <div
          class="relative overflow-hidden bg-gradient-to-br p-5 text-white"
          :class="channel.gradient"
        >
          <div
            class="pointer-events-none absolute -right-12 -top-16 size-40 rounded-full bg-white/20 blur-2xl"
          />
          <div class="relative flex items-start justify-between gap-4">
            <div class="flex min-w-0 gap-4">
              <div
                class="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/15 text-white shadow-sm ring-1 ring-white/25"
              >
                <i :class="channel.icon" class="text-2xl" />
              </div>
              <div class="min-w-0">
                <h3 class="mb-1 text-lg font-semibold text-white">
                  {{ channel.label }}
                </h3>
                <p class="text-sm leading-6 text-white/85">
                  {{ channel.description }}
                </p>
              </div>
            </div>
            <span
              class="shrink-0 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25"
            >
              {{ channel.connected ? 'Conectado' : 'Não conectado' }}
            </span>
          </div>
        </div>

        <div class="flex flex-1 flex-col p-5">
          <div class="rounded-2xl border border-n-weak bg-n-alpha-2 p-4">
            <div
              class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              Campo exigido
            </div>
            <p class="text-sm text-n-slate-12">{{ channel.requirement }}</p>
          </div>

          <div v-if="channel.records.length" class="mt-4 grid gap-2">
            <div
              v-for="inbox in channel.records"
              :key="inbox.id"
              class="flex items-center justify-between gap-3 rounded-2xl border border-n-weak bg-n-background px-4 py-3"
            >
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-n-slate-12">
                  {{ inbox.name }}
                </div>
                <div class="text-xs text-n-slate-10">Inbox #{{ inbox.id }}</div>
              </div>
              <div class="flex shrink-0 gap-2">
                <router-link
                  :to="
                    accountScopedRoute('settings_inbox_show', {
                      inboxId: inbox.id,
                    })
                  "
                >
                  <Button icon="i-lucide-settings" slate sm />
                </router-link>
                <Button
                  icon="i-lucide-unplug"
                  slate
                  sm
                  class="hover:enabled:!bg-n-ruby-3 hover:enabled:!text-n-ruby-11"
                  @click="openDisconnect(inbox)"
                />
              </div>
            </div>
          </div>

          <div
            class="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5"
          >
            <div class="text-xs text-n-slate-10">
              <template v-if="mirrorFor(channel.key)?.status">
                Acelerity: {{ mirrorFor(channel.key).status }}
              </template>
              <template v-else>
                Sincronize após conectar para atualizar a Acelerity.
              </template>
            </div>
            <router-link
              :to="
                accountScopedRoute('settings_inboxes_page_channel', {
                  sub_page: channel.route,
                })
              "
            >
              <Button
                :label="channel.connected ? 'Conectar outra conta' : 'Conectar'"
                icon="i-lucide-plug"
                size="sm"
              />
            </router-link>
          </div>
        </div>
      </article>
    </section>

    <woot-confirm-delete-modal
      v-if="showDisconnectPopup"
      v-model:show="showDisconnectPopup"
      :title="disconnectTitle"
      :message="disconnectMessage"
      :confirm-text="`Desconectar ${selectedInbox.name || ''}`"
      :reject-text="`Manter ${selectedInbox.name || 'canal'}`"
      :confirm-value="selectedInbox.name"
      confirm-place-holder-text="Digite o nome do canal para confirmar"
      @on-confirm="confirmDisconnect"
      @on-close="closeDisconnect"
    />
  </div>
</template>
